import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Instagram, Send } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = ["Branding", "Performance Marketing", "Web Development", "Social Media Management", "Media Production", "Kickstart Trial", "General"];

const CONTACTS = [
  { icon: Mail, label: "Email", value: "info@katzya.de", href: "mailto:info@katzya.de", testid: "contact-email-link" },
  { icon: Phone, label: "Phone", value: "+49 1575 5849826", href: "tel:+4915755849826", testid: "contact-phone-link" },
  { icon: Instagram, label: "Instagram", value: "@offical.katzya", href: "https://instagram.com/offical.katzya", testid: "contact-instagram-link" },
];

const inputCls =
  "w-full bg-[#FDFBF5] sketch-border px-4 py-3 text-sm sm:text-base text-[#2B0A4E] placeholder:text-[#5E4783]/50 focus:outline-none focus:ring-2 focus:ring-[#2B0A4E]/30 transition-shadow";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", service: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t("toast_success"));
      setForm({ name: "", email: "", service: "General", message: "" });
    } catch (err) {
      toast.error(t("toast_error"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <FadeUp>
          <Eyebrow>{t("contact_eyebrow")}</Eyebrow>
          <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2B0A4E] leading-tight" data-testid="contact-heading">
            {t("contact_h1")} <span className="italic gold-highlight">{t("contact_h2")}</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5E4783] leading-relaxed max-w-md" data-testid="contact-sub">
            {t("contact_sub")}
          </p>

          <div className="mt-10 space-y-4">
            {CONTACTS.map((c) => (
              <a
                key={c.label}
                data-testid={c.testid}
                href={c.href}
                target={c.label === "Instagram" ? "_blank" : undefined}
                rel="noreferrer"
                className="group flex items-center gap-4 sketch-border bg-[#FFFFFF] px-5 py-4 hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#8B5CF6] transition-[transform,box-shadow] duration-300"
              >
                <span className="w-10 h-10 rounded-full bg-[#F2C230] flex items-center justify-center shrink-0">
                  <c.icon size={18} className="text-[#2B0A4E]" />
                </span>
                <span>
                  <span className="block font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#2B0A4E]/50">{c.label}</span>
                  <span className="block text-sm sm:text-base font-medium text-[#2B0A4E] group-hover:underline underline-offset-4">{c.value}</span>
                </span>
              </a>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="sketch-border bg-[#FFFFFF] p-7 sm:p-10 shadow-[8px_8px_0_#F2C230] space-y-5"
          >
            <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[#2B0A4E]/50">
              {t("form_tag")}
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              <input
                data-testid="contact-form-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder={t("form_name")}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("form_email")}
                className={inputCls}
              />
            </div>
            <select
              data-testid="contact-form-service"
              value={form.service}
              onChange={set("service")}
              className={inputCls}
            >
              {SERVICE_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder={t("form_message")}
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-form-submit"
              type="submit"
              disabled={sending}
              className="sketch-border w-full bg-[#2B0A4E] text-[#FDFBF5] px-7 py-4 font-mono-label text-xs tracking-[0.2em] uppercase flex items-center justify-center gap-3 hover:bg-[#3E116B] hover:-translate-y-0.5 transition-[transform,background-color] duration-300 disabled:opacity-60 disabled:translate-y-0"
            >
              {sending ? t("form_sending") : t("form_submit")}
              <Send size={15} />
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
